import { Enable, html, on, rune, View } from 'rune-ts';
import klass from './LandingTextarea.module.scss';
import { ButtonClose } from '../../atoms/ButtonClose/ButtonClose';
import { typoPcMo } from '../../../../shared/typography/typo';

export class TextareaFocusoutEvent extends CustomEvent<never> {}
export class TextareaFocusinEvent extends CustomEvent<never> {}
export class TextareaChangedEvent extends CustomEvent<never> {}
export class TextareaResetEvent extends CustomEvent<boolean> {}
export class TextareaResetAfterEvent extends CustomEvent<boolean> {}

export type LandingTextareaProps = {
  name: string;
  value?: string;
  placeholder: string;
  rows?: number;
  maxLength?: number;
  custom_klass?: string;
};

export class LandingTextareaSideController extends View<LandingTextareaProps> {
  resetButton = new ButtonClose({ hide: true, tag: 'span' });

  protected override template() {
    return html` <div class="${klass.controller}">${this.resetButton}</div>`;
  }

  protected override onRender() {
    this.delegate('click', ButtonClose, () => {
      this.dispatchEvent(TextareaResetEvent, { bubbles: true });
    });
  }

  override redraw(): this {
    const { value = '' } = this.data;

    if (value && this.resetButton.data.hide) {
      this.resetButton.show();
    }

    if (!value) {
      this.resetButton.immediateHide();
    }

    return this;
  }

  focusout() {
    this.resetButton.hide();
  }
}

export class LandingTextareaSideControllerEnable extends Enable<LandingTextareaProps> {
  constructor(public override view: LandingTextarea) {
    super(view);
  }

  @on(TextareaResetEvent)
  resetHandler() {
    this.data.value = '';
    this.view.resetTextarea();
    this.view.textareaSideView.redraw();
    this.dispatchEvent(TextareaResetAfterEvent, {});
  }

  @on(TextareaFocusinEvent)
  @on(TextareaChangedEvent)
  redrawHandler() {
    this.view.textareaSideView.redraw();
  }

  @on(TextareaFocusoutEvent)
  focusoutHandler() {
    this.view.textareaSideView.focusout();
  }
}

export class LandingTextarea extends View<LandingTextareaProps> {
  textareaSideView: LandingTextareaSideController;
  textareaSideViewEnable: LandingTextareaSideControllerEnable;

  constructor(data: LandingTextareaProps) {
    super(data);

    this.textareaSideView = new LandingTextareaSideController(data);
    this.textareaSideViewEnable = new LandingTextareaSideControllerEnable(this);
  }

  override template() {
    const { name, placeholder, rows = 2, maxLength = 2000, custom_klass = '' } = this.data;
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html`
      <div class="${klass.text_wrapper} ${custom_klass}">
        <textarea
          name=${name}
          class="${klass.textarea} ${typoPcMo({
            is_mobile,
            pc: '16_medium',
            mo: '14_medium',
          })}"
          placeholder="${placeholder}"
          maxlength="${maxLength}"
          rows="${rows}"
        ></textarea>
        ${this.textareaSideView}
      </div>
    `;
  }

  resetTextarea() {
    const textarea = this.element().querySelector('textarea');

    if (textarea) {
      textarea.value = '';
      this.data.value = '';
    }
  }

  getValue() {
    return this.element().querySelector('textarea')?.value || '';
  }

  protected override onRender() {
    this.setAutoHeightInTextArea();

    this.delegate('focusin', 'textarea', () => {
      this.data.value = this.getValue();
      this.dispatchEvent(TextareaFocusinEvent, {});
    });
    this.delegate('focusout', 'textarea', () => {
      this.data.value = this.getValue();
      this.dispatchEvent(TextareaFocusoutEvent, {});
    });
    this.delegate('change', 'textarea', () => {
      this.data.value = this.getValue();
      this.dispatchEvent(TextareaChangedEvent, {});
    });
    this.delegate('keyup', 'textarea', () => {
      this.data.value = this.getValue();
      this.dispatchEvent(TextareaChangedEvent, {});
    });
    this.delegate('input', 'textarea', () => {
      this.setAutoHeightInTextArea();
    });
    this.addEventListener(TextareaResetAfterEvent, () => {
      this.setAutoHeightInTextArea();
    });
  }

  setAutoHeightInTextArea() {
    const text_area = this.element().querySelector('textarea');
    const text_area_wrapper = this.element();

    if (text_area && text_area_wrapper) {
      text_area.style.height = 'auto';
      text_area.style.height = `${text_area.scrollHeight / 14}rem`;

      if (text_area.value) {
        text_area_wrapper.classList.add(klass.border_active);
      } else {
        text_area_wrapper.classList.remove(klass.border_active);
      }
    }
  }
}
